
import React, {
  forwardRef,
  useMemo,
} from "react";

import {
  fmtDate,
  fmtDateTime,
  getBudgetCodeLabel,
  getClaimantLabel,
  getEventDatePlace,
  getEventLabel,
  getEventMeta,
  isManualExpenseClaim,
  money,
  statusLabel,
  toAbsoluteFileUrl,
} from "../utils/expenseAdminFormat";

/* ==========================================================================
   CONFIGURATION
   ========================================================================== */

const LOGO_SRC = "/logo_NA_fondblanc.jpg";

/* ==========================================================================
   HELPERS
   ========================================================================== */

function safe(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return value;
}

function num(value) {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
}

function formatKm(value) {
  return num(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
}

function formatRate(value) {
  return num(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function isDetailedMileage(claim) {
  return Boolean(
    claim?.mileage_detail?.result?.aller
  );
}

/* ==========================================================================
   COMPACT SECTION
   ========================================================================== */

function CompactSection({
  title,
  children,
  className = "",
}) {
  return (
    <section
      className={`break-inside-avoid ${className}`}
    >
      <div className="mb-1.5 border-b border-slate-300 pb-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-700">
        {title}
      </div>

      {children}
    </section>
  );
}

/* ==========================================================================
   INFO TABLE
   ========================================================================== */

function InfoTable({ rows = [] }) {
  return (
    <table className="w-full border-collapse text-[9px] leading-tight">
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={`${row.label}-${index}`}
            className="border-b border-slate-200"
          >
            <td className="w-[34%] bg-slate-50 px-2 py-1.5 font-bold uppercase tracking-[0.08em] text-slate-500">
              {row.label}
            </td>

            <td className="break-words px-2 py-1.5 font-semibold text-slate-900">
              {safe(row.value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ==========================================================================
   MINI TOTAL
   ========================================================================== */

function MiniTotal({
  label,
  value,
  strong = false,
}) {
  return (
    <div
      className={[
        "rounded-xl border px-3 py-2",
        strong
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-slate-50 text-slate-900",
      ].join(" ")}
    >
      <div
        className={[
          "text-[8px] font-bold uppercase tracking-[0.14em]",
          strong
            ? "text-slate-300"
            : "text-slate-500",
        ].join(" ")}
      >
        {label}
      </div>

      <div className="mt-0.5 text-[13px] font-black">
        {value}
      </div>
    </div>
  );
}

/* ==========================================================================
   DETAIL D'UN TRAJET KILOMETRIQUE
   ========================================================================== */

function MileageJourneyPrint({
  title,
  journey,
  declaredJourney,
}) {
  if (!journey || !Array.isArray(journey.legs)) {
    return null;
  }

  const stops = Array.isArray(declaredJourney?.stops)
    ? declaredJourney.stops
    : [];

  return (
    <div className="mb-2">
      {/* HEADER */}

      <div className="flex items-center justify-between border border-slate-300 bg-slate-100 px-2 py-1.5">
        <div className="text-[9px] font-black uppercase text-slate-900">
          {title}
        </div>

        <div className="text-[9px] font-black text-slate-900">
          {formatKm(journey.distance_km)} km
          {" · "}
          {money(journey.montant_trajet)}
        </div>
      </div>

      {/* TABLEAU DES TRONCONS */}

      <table className="w-full table-fixed border-collapse text-[8px] leading-tight">
        <colgroup>
          <col style={{ width: "49%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "14%" }} />
        </colgroup>

        <thead>
          <tr className="bg-slate-50 text-slate-600">
            <th className="border border-slate-200 px-2 py-1.5 text-left">
              Trajet
            </th>

            <th className="border border-slate-200 px-1 py-1.5 text-right">
              Km
            </th>

            <th className="border border-slate-200 px-1 py-1.5 text-center">
              Pass.
            </th>

            <th className="border border-slate-200 px-1 py-1.5 text-right">
              €/km
            </th>

            <th className="border border-slate-200 px-1 py-1.5 text-right">
              Montant
            </th>
          </tr>
        </thead>

        <tbody>
          {journey.legs.map((leg, index) => (
            <tr
              key={`${title}-${index}`}
              className="break-inside-avoid"
            >
              <td className="border border-slate-200 px-2 py-1.5 align-top">
                <div className="break-words font-bold text-slate-800">
                  {leg.origin}
                </div>

                <div className="mt-0.5 break-words text-slate-500">
                  → {leg.destination}
                </div>
              </td>

              <td className="border border-slate-200 px-1 py-1.5 text-right align-top">
                {formatKm(leg.distance_km)}
              </td>

              <td className="border border-slate-200 px-1 py-1.5 text-center align-top">
                {num(leg.passengers)}
              </td>

              <td className="border border-slate-200 px-1 py-1.5 text-right align-top">
                {formatRate(leg.rate)}
              </td>

              <td className="border border-slate-200 px-1 py-1.5 text-right align-top font-black">
                {money(leg.amount)}
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr className="bg-slate-100 font-black">
            <td className="border border-slate-300 px-2 py-1.5">
              Total {title.toLowerCase()}
            </td>

            <td className="border border-slate-300 px-1 py-1.5 text-right">
              {formatKm(journey.distance_km)}
            </td>

            <td
              colSpan={2}
              className="border border-slate-300"
            />

            <td className="border border-slate-300 px-1 py-1.5 text-right">
              {money(journey.montant_trajet)}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* PASSAGERS AU DEPART */}

      {num(declaredJourney?.initialPassengers) > 0 && (
        <div className="mt-1 text-[8px] text-slate-600">
          Passagers au départ :{" "}
          <strong>
            {declaredJourney.initialPassengers}
          </strong>
        </div>
      )}

      {/* MOUVEMENTS DE PASSAGERS */}

      {stops.length > 0 && (
        <div className="mt-1.5 break-inside-avoid rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
          <div className="mb-1 text-[8px] font-black uppercase tracking-wide text-slate-600">
            Prises en charge et déposes
          </div>

          <div className="space-y-1">
            {stops.map((stop, index) => {
              const boarding = num(stop.boarding);
              const alighting = num(stop.alighting);

              return (
                <div
                  key={`${title}-stop-${index}`}
                  className="break-inside-avoid border-b border-slate-200 pb-1 text-[8px] last:border-0 last:pb-0"
                >
                  <span className="font-bold text-slate-800">
                    {index + 1}. {safe(stop.address)}
                  </span>

                  <span className="ml-2 text-slate-600">
                    {boarding > 0
                      ? `+${boarding} montée(s)`
                      : ""}

                    {boarding > 0 && alighting > 0
                      ? " · "
                      : ""}

                    {alighting > 0
                      ? `-${alighting} descente(s)`
                      : ""}

                    {boarding === 0 && alighting === 0
                      ? "Arrêt intermédiaire"
                      : ""}
                  </span>

                  {stop.names && (
                    <div className="mt-0.5 break-words text-slate-500">
                      Personnes : {stop.names}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   DETAIL COMPLET DES INDEMNITES KILOMETRIQUES
   ========================================================================== */

function MileageDetailsPrint({ claim }) {
  const detail = claim?.mileage_detail;
  const result = detail?.result;

  if (!result?.aller) return null;

  const outbound = detail.outbound;
  const inbound = detail.inbound;

  return (
    <section className="mb-3">
      <div className="mb-2 border-b-2 border-slate-800 pb-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-900">
        Détail du calcul des indemnités kilométriques
      </div>

      {/* SYNTHESE */}

      <div className="mb-2 grid grid-cols-3 gap-2 break-inside-avoid">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2">
          <div className="text-[8px] font-bold uppercase text-slate-500">
            Distance totale
          </div>

          <div className="mt-0.5 text-[12px] font-black">
            {formatKm(claim.distance_km)} km
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2">
          <div className="text-[8px] font-bold uppercase text-slate-500">
            Taux moyen pondéré
          </div>

          <div className="mt-0.5 text-[12px] font-black">
            {formatRate(result.taux_moyen)} €/km
          </div>
        </div>

        <div className="rounded-lg border border-slate-900 bg-slate-900 px-2 py-2 text-white">
          <div className="text-[8px] font-bold uppercase text-slate-300">
            Indemnité trajet
          </div>

          <div className="mt-0.5 text-[12px] font-black">
            {money(claim.montant_trajet)}
          </div>
        </div>
      </div>

      {/* ALLER */}

      <MileageJourneyPrint
        title="Trajet aller"
        journey={result.aller}
        declaredJourney={outbound}
      />

      {/* RETOUR */}

      {result.retour && (
        <MileageJourneyPrint
          title="Trajet retour"
          journey={result.retour}
          declaredJourney={inbound}
        />
      )}

      <div className="mt-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-[8px] leading-snug text-slate-600 break-inside-avoid">
        Indemnités calculées par tronçon selon la distance
        routière et le nombre de passagers à bord.
        Le taux moyen est fourni à titre indicatif.
        Le montant enregistré fait foi.
      </div>
    </section>
  );
}

/* ==========================================================================
   PRINT SHEET
   ========================================================================== */

const ExpenseClaimPrintSheet = forwardRef(
  function ExpenseClaimPrintSheet(
    {
      claim,
      eventDetailsMap = {},
    },
    ref,
  ) {
    /* ======================================================================
       ATTACHMENTS
       ====================================================================== */

    const itemAttachmentsMap = useMemo(() => {
      const map = new Map();

      (claim?.ClaimAttachments || []).forEach(
        (attachment) => {
          if (!attachment.expense_claim_item_id) {
            return;
          }

          const key =
            attachment.expense_claim_item_id;

          if (!map.has(key)) {
            map.set(key, []);
          }

          map.get(key).push(attachment);
        },
      );

      return map;
    }, [claim]);

    const generalAttachments = useMemo(
      () =>
        (claim?.ClaimAttachments || []).filter(
          (attachment) =>
            !attachment.expense_claim_item_id,
        ),
      [claim],
    );

    /* ======================================================================
       DATA
       ====================================================================== */

    const totalIndemnites =
      Number(claim?.indemnite_tenue || 0) +
      Number(claim?.indemnite_representation || 0);

    const claimantLabel =
      getClaimantLabel(claim);

    const manualEvent =
      isManualExpenseClaim(claim);

    const eventLabel =
      getEventLabel(claim, eventDetailsMap);

    const eventDatePlace =
      getEventDatePlace(claim, eventDetailsMap);

    const eventMeta =
      getEventMeta(claim, eventDetailsMap);

    const validatorLabel =
      claim?.ClaimValidator
        ? `${claim.ClaimValidator.prenom || ""} ${
            claim.ClaimValidator.nom || ""
          }`.trim()
        : "—";

    const generatedAt =
      fmtDateTime(new Date());

    const detailedMileage =
      isDetailedMileage(claim);

    /* ======================================================================
       JSX
       ====================================================================== */

    return (
      <div
        ref={ref}
        className="bg-white text-slate-900"
      >
        <div className="mx-auto w-[198mm] bg-white px-0 py-0 font-satoshi text-slate-900">

          {/* ================================================================
              HEADER
             ================================================================ */}

          <header className="mb-3 flex items-center justify-between gap-4 border-b-2 border-slate-900 pb-2 break-inside-avoid">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_SRC}
                alt="Ligue Judo Nouvelle-Aquitaine"
                className="h-12 w-auto object-contain"
              />

              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Ligue Judo Nouvelle-Aquitaine
                </div>

                <h1 className="mt-0.5 text-[18px] font-black leading-none text-slate-950">
                  Fiche de frais
                </h1>

                <div className="mt-1 text-[9px] font-semibold text-slate-500">
                  Fiche #{claim?.id} · Éditée le {generatedAt}
                </div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">
                Statut
              </div>

              <div className="mt-1 rounded-full border border-slate-300 px-3 py-1 text-[10px] font-black uppercase text-slate-900">
                {statusLabel(claim?.statut)}
              </div>
            </div>
          </header>

          {/* ================================================================
              DEMANDEUR + EVENEMENT
             ================================================================ */}

          <div className="mb-3 grid grid-cols-[1.1fr_1fr] gap-3">
            <CompactSection title="Demandeur">
              <InfoTable
                rows={[
                  {
                    label: "Nom / prénom",
                    value: claimantLabel,
                  },
                  {
                    label: "Email",
                    value: claim?.Claimant?.email,
                  },
                  {
                    label: "Code budgétaire",
                    value: getBudgetCodeLabel(claim),
                  },
                  {
                    label: "Validateur",
                    value: validatorLabel,
                  },
                ]}
              />
            </CompactSection>

            <CompactSection
              title={
                manualEvent
                  ? "Réunion hors calendrier"
                  : "Événement"
              }
            >
              <InfoTable
                rows={[
                  {
                    label: manualEvent
                      ? "Réunion"
                      : "Événement",
                    value: eventLabel,
                  },
                  {
                    label: "Date / lieu",
                    value: eventDatePlace,
                  },
                  {
                    label: "Origine",
                    value: manualEvent
                      ? "Saisie manuelle"
                      : eventMeta,
                  },
                  {
                    label: "Soumise le",
                    value: fmtDateTime(
                      claim?.date_soumission,
                    ),
                  },
                ]}
              />
            </CompactSection>
          </div>

          {/* ================================================================
              TOTAUX
             ================================================================ */}

          <div className="mb-3 grid grid-cols-4 gap-2 break-inside-avoid">
            <MiniTotal
              label="Total général"
              value={money(claim?.total_general)}
              strong
            />

            <MiniTotal
              label="Autres frais"
              value={money(claim?.total_frais)}
            />

            <MiniTotal
              label="Trajet"
              value={money(claim?.montant_trajet)}
            />

            <MiniTotal
              label="Indemnités"
              value={money(totalIndemnites)}
            />
          </div>

          {/* ================================================================
              TRAJET ET SUIVI
             ================================================================ */}

          <div className="mb-3 grid grid-cols-[1fr_0.9fr] gap-3">
            <CompactSection title="Trajet et transport">
              <InfoTable
                rows={[
                  {
                    label: "Trajet nécessaire",
                    value: claim?.trajet_necessaire
                      ? "Oui"
                      : "Non",
                  },
                  {
                    label: "Transport",
                    value: claim?.moyen_transport,
                  },
                  {
                    label: "Aller / retour",
                    value: claim?.aller_retour
                      ? "Oui"
                      : "Non",
                  },
                  {
                    label: "Départ",
                    value: claim?.depart,
                  },
                  {
                    label: "Arrivée",
                    value: claim?.arrivee,
                  },
                  {
                    label: detailedMileage
                      ? "Distance totale"
                      : "Distance aller",
                    value: claim?.distance_km != null
                      ? `${formatKm(claim.distance_km)} km`
                      : "—",
                  },
                  {
                    label: detailedMileage
                      ? "Taux moyen"
                      : "Taux km",
                    value: claim?.taux_km != null
                      ? `${formatRate(claim.taux_km)} €/km`
                      : "—",
                  },
                  {
                    label: "Covoiturage",
                    value: claim?.covoiturage
                      ? `${
                          claim?.covoiturage_role || "Oui"
                        }${
                          claim?.covoiturage_avec
                            ? ` avec ${claim.covoiturage_avec}`
                            : ""
                        }${
                          claim?.covoiturage_nb_passagers
                            ? ` · ${claim.covoiturage_nb_passagers} passager(s)`
                            : ""
                        }`
                      : "Non",
                  },
                ]}
              />
            </CompactSection>

            <CompactSection title="Suivi administratif">
              <InfoTable
                rows={[
                  {
                    label: "Créée le",
                    value: fmtDateTime(claim?.created_at),
                  },
                  {
                    label: "Mise à jour",
                    value: fmtDateTime(claim?.updated_at),
                  },
                  {
                    label: "Tenue",
                    value: money(claim?.indemnite_tenue),
                  },
                  {
                    label: "Représentation",
                    value: money(
                      claim?.indemnite_representation,
                    ),
                  },
                  {
                    label: "Montant trajet",
                    value: money(claim?.montant_trajet),
                  },
                ]}
              />
            </CompactSection>
          </div>

          {/* ================================================================
              NOUVEAU DETAIL KILOMETRIQUE
             ================================================================ */}

          {detailedMileage && (
            <MileageDetailsPrint claim={claim} />
          )}

          {/* ================================================================
              DETAIL DES FRAIS
             ================================================================ */}

          <section className="mb-3">
            <div className="mb-1.5 border-b border-slate-300 pb-1 text-[9px] font-black uppercase tracking-[0.16em] text-slate-700">
              Détail des frais
            </div>

            {!claim?.ClaimItems?.length ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[9px] font-semibold text-slate-500 break-inside-avoid">
                Aucune ligne de frais.
              </div>
            ) : (
              <table className="w-full border-collapse text-[8.5px] leading-tight">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="border border-slate-900 px-2 py-1.5 text-left font-black uppercase">
                      Date
                    </th>

                    <th className="border border-slate-900 px-2 py-1.5 text-left font-black uppercase">
                      Type
                    </th>

                    <th className="border border-slate-900 px-2 py-1.5 text-left font-black uppercase">
                      Description
                    </th>

                    <th className="border border-slate-900 px-2 py-1.5 text-right font-black uppercase">
                      Montant
                    </th>

                    <th className="border border-slate-900 px-2 py-1.5 text-left font-black uppercase">
                      Justif.
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {claim.ClaimItems.map((item) => {
                    const itemAttachments =
                      itemAttachmentsMap.get(item.id) || [];

                    return (
                      <tr
                        key={item.id}
                        className="break-inside-avoid border-b border-slate-200"
                      >
                        <td className="w-[16mm] border-x border-slate-200 px-2 py-1.5 align-top font-semibold text-slate-700">
                          {fmtDate(item?.date_frais)}
                        </td>

                        <td className="w-[27mm] border-x border-slate-200 px-2 py-1.5 align-top font-bold text-slate-900">
                          {safe(item?.type)}
                        </td>

                        <td className="border-x border-slate-200 px-2 py-1.5 align-top text-slate-700">
                          {safe(item?.description)}
                        </td>

                        <td className="w-[22mm] border-x border-slate-200 px-2 py-1.5 text-right align-top font-black text-slate-900">
                          {money(item?.montant)}
                        </td>

                        <td className="w-[28mm] border-x border-slate-200 px-2 py-1.5 align-top text-slate-600">
                          {!itemAttachments.length ? (
                            "—"
                          ) : (
                            <div className="space-y-0.5">
                              {itemAttachments
                                .slice(0, 2)
                                .map((attachment) => (
                                  <div key={attachment.id}>
                                    <a
                                      href={toAbsoluteFileUrl(
                                        attachment.file_url,
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="break-words font-semibold text-sky-700 underline"
                                    >
                                      {attachment.original_name ||
                                        `Justif. #${attachment.id}`}
                                    </a>
                                  </div>
                                ))}

                              {itemAttachments.length > 2 && (
                                <div className="font-semibold text-slate-500">
                                  + {itemAttachments.length - 2} autre(s)
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="break-inside-avoid">
                    <td
                      colSpan={3}
                      className="border border-slate-900 bg-slate-100 px-2 py-1.5 text-right text-[9px] font-black uppercase text-slate-700"
                    >
                      Total général
                    </td>

                    <td className="border border-slate-900 bg-slate-900 px-2 py-1.5 text-right text-[10px] font-black text-white">
                      {money(claim?.total_general)}
                    </td>

                    <td className="border border-slate-900 bg-slate-100" />
                  </tr>
                </tfoot>
              </table>
            )}
          </section>

          {/* ================================================================
              COMMENTAIRES
             ================================================================ */}

          <div className="mb-3 grid grid-cols-2 gap-3 break-inside-avoid">
            <CompactSection title="Commentaire demandeur">
              <div className="min-h-[22mm] break-words rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-[9px] leading-snug text-slate-700">
                {safe(claim?.commentaire)}
              </div>
            </CompactSection>

            <CompactSection title="Commentaire administrateur">
              <div className="min-h-[22mm] break-words rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-[9px] leading-snug text-slate-700">
                {safe(claim?.admin_commentaire)}
              </div>
            </CompactSection>
          </div>

          {/* ================================================================
              JUSTIFICATIFS ET SIGNATURES
             ================================================================ */}

          <div className="mb-3 grid grid-cols-2 gap-3 break-inside-avoid">
            <CompactSection title="Justificatifs généraux">
              {!generalAttachments.length ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[9px] font-semibold text-slate-500">
                  Aucun justificatif général.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 text-[8.5px]">
                  {generalAttachments
                    .slice(0, 6)
                    .map((attachment) => (
                      <a
                        key={attachment.id}
                        href={toAbsoluteFileUrl(
                          attachment.file_url,
                        )}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-sky-700 underline"
                      >
                        {attachment.original_name ||
                          `Justificatif #${attachment.id}`}
                      </a>
                    ))}

                  {generalAttachments.length > 6 && (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-500">
                      + {generalAttachments.length - 6} autre(s)
                    </span>
                  )}
                </div>
              )}
            </CompactSection>

            <CompactSection title="Signatures">
              <div className="grid grid-cols-2 gap-3 text-[8.5px]">
                <div>
                  <div className="font-bold uppercase tracking-[0.12em] text-slate-500">
                    Demandeur
                  </div>

                  <div className="mt-7 border-t border-slate-400" />
                </div>

                <div>
                  <div className="font-bold uppercase tracking-[0.12em] text-slate-500">
                    Visa administratif
                  </div>

                  <div className="mt-7 border-t border-slate-400" />
                </div>
              </div>
            </CompactSection>
          </div>

          {/* ================================================================
              FOOTER
             ================================================================ */}

          <footer className="border-t border-slate-300 pt-1.5 text-[7.5px] font-medium text-slate-500">
            Ligue Judo Nouvelle-Aquitaine · Fiche de frais
            générée depuis l'espace administrateur · Les
            justificatifs restent consultables depuis les
            liens associés à la fiche.
          </footer>
        </div>
      </div>
    );
  },
);

export default ExpenseClaimPrintSheet;
